import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import ConfirmHost from "./components/ConfirmModal";
import RiderTracker from "./pages/RiderTracker";
import CPDTrack from "./pages/CPDTrack";
import CPDDriverOptin from "./pages/CPDDriverOptin";
import CPDIntakeForm from "./pages/CPDIntakeForm";
import "@/App.css";

import { AuthProvider, useAuth } from "./lib/auth";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ConciergeWidget from "./components/ConciergeWidget";
import Home from "./pages/Home";
import BookTrip from "./pages/BookTrip";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import TPGodView from "./pages/admin/TPGodView";
import TrackTrip from "./pages/TrackTrip";
import TrackShare from "./pages/TrackShare";
import DriverPortal from "./pages/DriverPortal";
import PartnerSignup from "./pages/legacy/PartnerSignup";
import ServiceLanding from "./pages/ServiceLanding";
import MeetingRoom from "./pages/MeetingRoom";
import PublicSchedule from "./pages/PublicSchedule";
import ConfirmTrip from "./pages/ConfirmTrip";
import SmsTerms from "./pages/SmsTerms";
import Privacy from "./pages/Privacy";
import Unsubscribe from "./pages/Unsubscribe";
import DriverOnboard from "./pages/DriverOnboard";
import DocUploadPage from "./pages/DocUploadPage";
import DriverTripApp from "./pages/driver/DriverTripApp";
import DriverBeacon from "./pages/driver/DriverBeacon";
import BeaconShortLink from "./pages/driver/BeaconShortLink";
import TPPortal from "./pages/tp/TPPortal";
import PartnerApply from "./pages/tp/PartnerApply";
import DriverApply from "./pages/legacy/DriverApply";
import RiderApp from "./pages/RiderApp";
import RiderReceipt from "./pages/RiderReceipt";
import VerifyHash from "./pages/VerifyHash";
import RiderSignup from "./pages/RiderSignup";
import TermsPage from "./pages/TermsPage";
import TPContractSign from "./pages/TPContractSign";
import { PaySuccess, PayCancel } from "./pages/Pay";
import FloorPage from "./pages/floor/Floor";
import ApplyHub from "./pages/ApplyHub";

function ScrollToAnchor() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function PublicLayout({ children, showConcierge = true }) {
  return (
    <>
      <Header />
      {children}
      <Footer />
      {showConcierge && <ConciergeWidget />}
    </>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-bone">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicLayout>
            <Home />
          </PublicLayout>
        }
      />
      <Route
        path="/book"
        element={
          <PublicLayout showConcierge={false}>
            <BookTrip />
          </PublicLayout>
        }
      />
      <Route
        path="/partners"
        element={
          <PublicLayout>
            <PartnerSignup />
          </PublicLayout>
        }
      />
      <Route
        path="/services/:slug"
        element={
          <PublicLayout>
            <ServiceLanding />
          </PublicLayout>
        }
      />
      <Route path="/driver/:pin" element={<DriverPortal />} />
      <Route path="/meet/:roomId" element={<MeetingRoom />} />
      <Route
        path="/schedule/:hostSlug"
        element={
          <PublicLayout showConcierge={false}>
            <PublicSchedule />
          </PublicLayout>
        }
      />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route path="/t/:id" element={<TrackTrip />} />
      <Route path="/track/:token" element={<TrackShare />} />
      <Route path="/confirm/:token" element={<ConfirmTrip />} />
      <Route path="/sms-terms" element={<PublicLayout showConcierge={false}><SmsTerms /></PublicLayout>} />
      <Route path="/privacy" element={<PublicLayout showConcierge={false}><Privacy /></PublicLayout>} />
      <Route path="/pay/success" element={<PaySuccess />} />
      <Route path="/pay/cancel" element={<PayCancel />} />
      <Route path="/unsubscribe" element={<PublicLayout showConcierge={false}><Unsubscribe /></PublicLayout>} />
      <Route path="/driver/onboard/:inviteCode" element={<DriverOnboard />} />
      <Route path="/upload/:token" element={<DocUploadPage />} />
      <Route path="/d/:token" element={<DriverTripApp />} />
      <Route path="/r/:token" element={<RiderTracker />} />
      <Route path="/cpd-track/:token" element={<CPDTrack />} />
      <Route path="/cpd-driver/:token" element={<CPDDriverOptin />} />
      <Route path="/cpd-form/:token" element={<CPDIntakeForm />} />
      <Route path="/beacon/:key" element={<DriverBeacon />} />
      <Route path="/b/:code" element={<BeaconShortLink />} />
      <Route path="/tp/contract/:token" element={<TPContractSign />} />
      <Route path="/tp/*" element={<TPPortal />} />
      <Route path="/partners/apply" element={<PartnerApply />} />
      {/* Unified entry point — picks driver vs provider, then routes into the
          existing forms above. No new backend, just easier wayfinding. */}
      <Route path="/apply" element={<PublicLayout showConcierge={false}><ApplyHub /></PublicLayout>} />
      <Route path="/onboard" element={<PublicLayout showConcierge={false}><ApplyHub /></PublicLayout>} />
      {/* One compliant driver application, mounted on every historical path so
          no driver can go active without background-check fields (see rider.py
          driver-apply retirement + tp_trips.py /drivers/apply). */}
      <Route path="/drivers/apply" element={<DriverApply />} />
      <Route path="/drivers/apply-legacy" element={<DriverApply />} />
      <Route path="/drive/new" element={<DriverApply />} />
      <Route path="/ride/new" element={<RiderSignup />} />
      <Route path="/terms" element={<PublicLayout showConcierge={false}><TermsPage /></PublicLayout>} />
      <Route path="/ride/:token" element={<RiderApp />} />
      <Route path="/ride/:token/receipt/:tripId" element={<RiderReceipt />} />
      <Route path="/verify/:hash" element={<VerifyHash />} />

      {/* The Floor — dispatcher workspace (frontend shell, mock data) */}
      <Route path="/floor" element={<ProtectedRoute><FloorPage /></ProtectedRoute>} />
      <Route path="/floor/providers" element={<ProtectedRoute><FloorPage /></ProtectedRoute>} />
      <Route path="/floor/preview" element={<FloorPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/providers/:tpId/god-view"
        element={
          <ProtectedRoute>
            <TPGodView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/:section"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToAnchor />
        <AppRoutes />
        <ConfirmHost />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: "Manrope, sans-serif",
              border: "1px solid #E2E0D8",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
